import type { LandSearchFilters } from '../pages/Explore/DeveloperMap/Lands/types/landFilters.types'
import type { BuildingSearchFilters } from '../pages/Explore/DeveloperMap/Buildings/types/buildingFilters.types'

/**
 * URL synchronization utilities for filter state management
 */

export type FilterType = 'lands' | 'buildings'

// Type for all possible filter values
type FilterValue =
  | string
  | number
  | boolean
  | { min?: number; max?: number }
  | string[]

/**
 * Converts filter values to URL-safe strings
 */
export const serializeFilterValue = (value: FilterValue): string => {
  if (value === null || value === undefined) return ''

  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return value.join(',')
    }

    // Range objects like { min: 100, max: 500 }
    if ('min' in value || 'max' in value) {
      const parts: string[] = []
      if (value.min !== undefined) parts.push(`min:${value.min}`)
      if (value.max !== undefined) parts.push(`max:${value.max}`)
      return parts.join('|')
    }
  }

  return String(value)
}

/**
 * Converts URL strings back to filter values
 */
export const deserializeFilterValue = (
  urlValue: string,
  originalValue?: FilterValue
): FilterValue => {
  if (!urlValue) return originalValue || ''

  // Handle range objects (min:100|max:500)
  if (urlValue.includes('min:') || urlValue.includes('max:')) {
    const range: { min?: number; max?: number } = {}
    const parts = urlValue.split('|')

    parts.forEach((part) => {
      if (part.startsWith('min:')) {
        range.min = Number(part.substring(4))
      } else if (part.startsWith('max:')) {
        range.max = Number(part.substring(4))
      }
    })

    return range
  }

  // Handle arrays (comma-separated)
  if (Array.isArray(originalValue)) {
    return urlValue.split(',').filter((v) => v.length > 0)
  }

  // Handle numbers
  if (typeof originalValue === 'number') {
    return Number(urlValue)
  }

  // Handle booleans
  if (typeof originalValue === 'boolean') {
    return urlValue === 'true'
  }

  // Return as string
  return urlValue
}

/**
 * Converts filters object to URL search params
 */
export const filtersToUrlParams = (
  filters: LandSearchFilters | BuildingSearchFilters,
  filterType: FilterType
): URLSearchParams => {
  const params = new URLSearchParams()

  // Add filter type
  params.set('type', filterType === 'lands' ? 'land' : 'building')

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      // Skip empty objects and arrays
      if (typeof value === 'object') {
        if (Array.isArray(value) && value.length === 0) return
        if (!Array.isArray(value) && Object.keys(value).length === 0) return
        if (
          'min' in value &&
          'max' in value &&
          value.min === undefined &&
          value.max === undefined
        )
          return
      }

      const serializedValue = serializeFilterValue(value)
      if (serializedValue) {
        params.set(key, serializedValue)
      }
    }
  })

  return params
}

/**
 * Converts URL search params back to filters object
 */
export const urlParamsToFilters = (
  searchParams: URLSearchParams,
  filterType: FilterType
): LandSearchFilters | BuildingSearchFilters => {
  const filters: any = {}

  // Get the reference filter structure to understand expected types
  const referenceFilters =
    filterType === 'lands'
      ? getLandFilterDefaults()
      : getBuildingFilterDefaults()

  searchParams.forEach((value, key) => {
    // Skip the 'type' parameter
    if (key === 'type') return

    // Get the expected type from reference filters
    const originalValue = referenceFilters[key as keyof typeof referenceFilters]
    filters[key] = deserializeFilterValue(value, originalValue)
  })

  return filters
}

/**
 * Default filter structures to understand expected types
 */
const getLandFilterDefaults = (): LandSearchFilters => ({
  mode: 'plot',
  location_search: '',
  plan: '',
  special_zone: 'display',
  no_building_zone: 'display',
  area: { min: undefined, max: undefined },
  bare_land: 'display',
  ownership: 'private',
  owners: { min: undefined, max: undefined },
  property_type: 'private',
  development_score: { min: undefined, max: undefined },
  under_exploited_score: { min: undefined, max: undefined },
  building_area: { min: undefined, max: undefined },
  building_year: { min: undefined, max: undefined },
  renovation_year: { min: undefined, max: undefined },
  floor_count: { min: undefined, max: undefined },
  building_height: { min: undefined, max: undefined },
  transaction_date: { min: undefined, max: undefined },
  purchase_price: { min: undefined, max: undefined },
  rent_price: { min: undefined, max: undefined },
  protected_building: 'display',
  polluted_zone: 'display',
  reserved_zone: 'display',
  underwater_protected_zone: 'display',
})

const getBuildingFilterDefaults = (): BuildingSearchFilters => ({
  mode: 'building',
  location_search: '',
  city_name: '',
  municipality_name: '',
  district_name: '',
  region_name: '',
  address_full: '',
  building_id: '',
  egid: undefined,
  city_id: '',
  municipality_id: '',
  district_id: '',
  region_id: '',
  area: { min: undefined, max: undefined },
  building_year: { min: undefined, max: undefined },
  floor_nb: { min: undefined, max: undefined },
  unit_nb: { min: undefined, max: undefined },
  height: { min: undefined, max: undefined },
  building_volume: { min: undefined, max: undefined },
  category: [],
  type: [],
  status: [],
  classification: [],
  popety_classification: [],
  heater_generator: [],
  minergie: [],
  heating_type: '',
  energy_efficiency: '',
  lake_view: [],
  mountain_view: [],
  public_transport_quality: [],
  view_quality: '',
  noise_level: '',
  transport_accessibility: '',
  solar_potential: '',
})

/**
 * Updates URL without triggering navigation
 */
export const updateUrlWithFilters = (
  filters: LandSearchFilters | BuildingSearchFilters,
  filterType: FilterType,
  replace: boolean = false
) => {
  const params = filtersToUrlParams(filters, filterType)
  const url = new URL(window.location.href)

  // Clear existing filter params
  url.search = ''

  // Add filter params
  params.forEach((value, key) => {
    url.searchParams.set(key, value)
  })

  // Update URL without navigation
  if (replace) {
    window.history.replaceState({}, '', url.toString())
  } else {
    window.history.pushState({}, '', url.toString())
  }
}

/**
 * Gets current filters from URL
 */
export const getFiltersFromUrl = (): {
  filters: LandSearchFilters | BuildingSearchFilters
  filterType: FilterType
} => {
  const params = new URLSearchParams(window.location.search)
  const typeParam = params.get('type')
  const filterType: FilterType =
    typeParam === 'building' ? 'buildings' : 'lands'

  const filters = urlParamsToFilters(params, filterType)

  return { filters, filterType }
}

/**
 * Checks if current URL has filter parameters
 */
export const hasFilterParams = (): boolean => {
  const params = new URLSearchParams(window.location.search)

  // Check if there are any params other than common navigation params
  const filterParams = Array.from(params.keys()).filter(
    (key) => !['mode', 'view'].includes(key)
  )

  return filterParams.length > 0
}
